import { useEffect, useState } from "react";
import { CalendarDays, CircleDollarSign, Flame, MapPin, MessageSquare } from "lucide-react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { ActionBar, Button, Field, SelectField, TextareaField } from "@fawkes/ui";

import { formatDate, formatEuro } from "../i18n/format";

// De tegel Crematie op het dossier, zoals de mockup hem tekent: wanneer (vorm, wie het invoerde,
// dag en tijd), het gekozen tarief (wat er nu op de factuur staat, de klasse, het
// identificatienummer), de asbestemming (waar de as heen gaat, het graf, het urnnummer) en een
// opmerking. Het tarief zelf is hier te lezen, niet te kiezen: het staat als regel op de factuur en
// dáár wordt het gewijzigd.

export interface CrematieGegevens {
  uitvaartvormId: string | null;
  verwerkingsvorm: string | null;
  ingevoerdDoor: string | null;
  planningDatum: string | null;
  crematoriumLocatie: string | null;
  identificatieNummer: string | null;
  asUrnNummer: string | null;
  bijzonderheden: string | null;
  retourwijze: string | null;
  grafnummer: string | null;
  gewichtsklasseNaam: string | null;
  gewichtsklasseId: string | null;
  /** Het gekozen tarief, en of het nog gewisseld mag worden. */
  tariefId: string | null;
  tariefWijzigbaar: boolean;
  /** De tariefregel op de factuur, als die er is. */
  tarief: { omschrijving: string | null; prijs: number | null; btwPercentage: number | null } | null;
}

/** De asbestemmingen uit de mockup; een eerder vrij ingevulde waarde blijft als eigen keuze staan. */
export const ASBESTEMMINGEN = ["URN_MEE", "VERSTROOIEN_VELD", "VERSTROOIEN_ZEE", "BIJZETTEN_GRAF"] as const;

interface Invoer {
  uitvaartvormId: string;
  tariefId: string;
  datum: string;
  tijd: string;
  crematoriumLocatie: string;
  identificatieNummer: string;
  asUrnNummer: string;
  bijzonderheden: string;
  retourwijze: string;
  grafnummer: string;
}

const vanGegevens = (d: CrematieGegevens): Invoer => ({
  uitvaartvormId: d.uitvaartvormId ?? "",
  tariefId: d.tariefId ?? "",
  datum: d.planningDatum ? d.planningDatum.slice(0, 10) : "",
  tijd: d.planningDatum ? d.planningDatum.slice(11, 16) : "",
  crematoriumLocatie: d.crematoriumLocatie ?? "",
  identificatieNummer: d.identificatieNummer ?? "",
  asUrnNummer: d.asUrnNummer ?? "",
  bijzonderheden: d.bijzonderheden ?? "",
  retourwijze: d.retourwijze ?? "",
  grafnummer: d.grafnummer ?? "",
});

/** Eén tarief om uit te kiezen; de server geeft ze per gewichtsklasse. */
interface TariefOptie {
  id: string;
  verwerkingsvorm: string;
  prijs: number | null;
  btwPercentage: number | null;
  ingangsdatum: string | null;
  einddatum: string | null;
}

export function CrematieDossierCrematieTegel({
  id,
  gegevens,
  onOpgeslagen,
}: {
  id: string;
  gegevens: CrematieGegevens;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  /** "Individueel · € 165,00 · versie 01-01-2026" — de vorm, de prijs en welke versie het is. */
  const tariefLabel = (o: TariefOptie) => [
    t(`crematoriumTarief.vorm.${o.verwerkingsvorm}`),
    o.prijs != null ? formatEuro(o.prijs, locale) : null,
    o.ingangsdatum ? t("crematieDetail.crematie.tariefVersie", { args: [formatDate(o.ingangsdatum, locale)] }) : null,
  ].filter(Boolean).join(" · ");
  const [invoer, setInvoer] = useState<Invoer>(() => vanGegevens(gegevens));
  const [notice, setNotice] = useState<"conflict" | "forbidden" | "error" | null>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  const sleutel = JSON.stringify(gegevens);
  useEffect(() => {
    setInvoer(vanGegevens(gegevens));
    setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel]);

  const { data: vormenData } = useCustom({ url: "crematorium/uitvaartvormen", method: "get", errorNotification: false });
  const vormen = (Array.isArray(vormenData?.data) ? vormenData.data : []) as { id: string; naam: string; actief: boolean }[];
  // De tarieven van deze gewichtsklasse: alle vormen en versies, om er een uit te kiezen.
  const { data: tariefData } = useCustom({
    url: `crematorium/aanmeldingen/tarieven?gewichtsklasseId=${gegevens.gewichtsklasseId ?? ""}`,
    method: "get",
    queryOptions: { enabled: gegevens.gewichtsklasseId != null },
    errorNotification: false,
  });
  const tarieven = (Array.isArray(tariefData?.data) ? tariefData.data : []) as TariefOptie[];

  const zet = <K extends keyof Invoer>(veld: K, waarde: Invoer[K]) =>
    setInvoer((huidig) => ({ ...huidig, [veld]: waarde }));
  const leeg = (s: string): string | null => (s.trim() === "" ? null : s.trim());

  const opslaan = () => {
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${id}/crematie`,
        method: "put",
        values: {
          uitvaartvormId: leeg(invoer.uitvaartvormId),
          // Alleen meesturen als het tarief werkelijk wisselt; anders herschrijft elke opslag de regel.
          tariefId: invoer.tariefId && invoer.tariefId !== (gegevens.tariefId ?? "") ? invoer.tariefId : null,
          // Dag en tijd worden samen één planningsmoment; zonder tijd telt het begin van de dag.
          planningDatum: invoer.datum ? `${invoer.datum}T${invoer.tijd || "00:00"}:00` : null,
          crematoriumLocatie: leeg(invoer.crematoriumLocatie),
          identificatieNummer: leeg(invoer.identificatieNummer),
          asUrnNummer: leeg(invoer.asUrnNummer),
          bijzonderheden: leeg(invoer.bijzonderheden),
          retourwijze: leeg(invoer.retourwijze),
          grafnummer: invoer.retourwijze === "BIJZETTEN_GRAF" ? leeg(invoer.grafnummer) : null,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onOpgeslagen(),
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice(code === 409 ? "conflict" : code === 403 ? "forbidden" : "error");
        },
      },
    );
  };

  const bestemmingen: { value: string; label: string }[] = [
    { value: "", label: "—" },
    ...ASBESTEMMINGEN.map((b) => ({ value: b, label: t(`crematieDetail.asbestemming.${b}`) })),
  ];
  if (invoer.retourwijze && !ASBESTEMMINGEN.includes(invoer.retourwijze as (typeof ASBESTEMMINGEN)[number])) {
    bestemmingen.push({ value: invoer.retourwijze, label: invoer.retourwijze });
  }

  const tarief = gegevens.tarief;

  return (
    <section className="fui-tile crem-tile crem-tile--crematie">
      <header className="fui-tile__header">
        <h4 className="fui-tile__title">
          <span className="fawkes-relatie-tiletitel"><Flame size={18} aria-hidden="true" />{t("crematieDetail.section.crematie")}</span>
        </h4>
      </header>

      <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`crematoriumTarief.notice.${notice}`)}
          </div>
        )}

        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><CalendarDays size={18} aria-hidden="true" />{t("crematieDetail.groep.wanneer")}</h5>
          <div className="fawkes-relatie-grid">
            <SelectField
              label={t("crematieDetail.kop.vorm")}
              value={invoer.uitvaartvormId}
              onChange={(e) => zet("uitvaartvormId", e.target.value)}
              options={[{ value: "", label: "—" }, ...vormen.filter((v) => v.actief).map((v) => ({ value: v.id, label: v.naam }))]}
            />
            <div className="crem-detail__info">
              <span className="crem-detail__info-label">{t("crematieDetail.crematie.ingevoerdDoor")}</span>
              <span className="crem-detail__info-value">{gegevens.ingevoerdDoor ?? "—"}</span>
            </div>
          </div>
          <div className="fawkes-relatie-grid">
            <Field label={t("crematieDetail.crematie.uitvaartdatum")} type="date" value={invoer.datum}
              onChange={(e) => zet("datum", e.target.value)} />
            <Field label={t("crematieDetail.crematie.tijd")} type="time" value={invoer.tijd}
              onChange={(e) => zet("tijd", e.target.value)} />
          </div>
          <div className="fawkes-relatie-grid crem-grid--1">
            <Field label={t("crematieDetail.crematie.locatie")} value={invoer.crematoriumLocatie}
              onChange={(e) => zet("crematoriumLocatie", e.target.value)} />
          </div>
        </section>

        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><CircleDollarSign size={18} aria-hidden="true" />{t("crematieDetail.groep.tarief")}</h5>
          <div className="fawkes-relatie-grid crem-grid--1">
            {gegevens.tariefWijzigbaar && tarieven.length > 0 ? (
              <div data-testid="tarief">
                <SelectField
                  label={t("crematieDetail.crematie.tarief")}
                  value={invoer.tariefId}
                  onChange={(e) => zet("tariefId", e.target.value)}
                  options={[
                    ...(invoer.tariefId ? [] : [{ value: "", label: t("crematieDetail.crematie.geenTarief") }]),
                    ...tarieven.map((o) => ({ value: o.id, label: tariefLabel(o) })),
                  ]}
                />
                <span className="crem-veldhint">{t("crematieDetail.crematie.tariefKiesHint")}</span>
              </div>
            ) : (
              <div className="crem-detail__info" data-testid="tarief">
                <span className="crem-detail__info-label">{t("crematieDetail.crematie.tarief")}</span>
                <span className="crem-detail__info-value">{tarief?.omschrijving ?? t("crematieDetail.crematie.geenTarief")}</span>
                <span className="crem-veldhint">{t("crematieDetail.crematie.tariefVast")}</span>
              </div>
            )}
          </div>
          <div className="fawkes-relatie-grid">
            <div className="crem-detail__info">
              <span className="crem-detail__info-label">{t("crematieDetail.crematie.basisprijs")}</span>
              <span className="crem-detail__info-value">
                {tarief?.prijs != null ? `${formatEuro(tarief.prijs, locale)} · ${tarief.btwPercentage ?? 0}%` : "—"}
              </span>
            </div>
            <div className="crem-detail__info">
              <span className="crem-detail__info-label">{t("crematieDetail.crematie.klasse")}</span>
              <span className="crem-detail__info-value">{gegevens.gewichtsklasseNaam ?? "—"}</span>
            </div>
          </div>
          <div className="fawkes-relatie-grid crem-grid--1">
            <Field label={t("crematieDetail.crematie.identificatie")} value={invoer.identificatieNummer}
              onChange={(e) => zet("identificatieNummer", e.target.value)} />
          </div>
        </section>

        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><MapPin size={18} aria-hidden="true" />{t("crematieDetail.groep.asbestemming")}</h5>
          <div className="fawkes-relatie-grid">
            <SelectField label={t("crematieDetail.crematie.bestemming")} value={invoer.retourwijze}
              onChange={(e) => zet("retourwijze", e.target.value)} options={bestemmingen} />
            {invoer.retourwijze === "BIJZETTEN_GRAF" ? (
              <Field label={t("crematieDetail.crematie.grafnummer")} value={invoer.grafnummer}
                onChange={(e) => zet("grafnummer", e.target.value)} />
            ) : (
              <Field label={t("crematieDetail.crematie.asUrn")} value={invoer.asUrnNummer}
                onChange={(e) => zet("asUrnNummer", e.target.value)} />
            )}
          </div>
          {invoer.retourwijze === "BIJZETTEN_GRAF" && (
            <div className="fawkes-relatie-grid crem-grid--1">
              <Field label={t("crematieDetail.crematie.asUrn")} value={invoer.asUrnNummer}
                onChange={(e) => zet("asUrnNummer", e.target.value)} />
            </div>
          )}
        </section>

        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><MessageSquare size={18} aria-hidden="true" />{t("crematieDetail.groep.opmerking")}</h5>
          <TextareaField label={t("crematieDetail.crematie.bijzonderheden")} value={invoer.bijzonderheden} rows={3}
            onChange={(e) => zet("bijzonderheden", e.target.value)} />
        </section>
      </div>

      <ActionBar>
        <Button variant="secondary" disabled={bezig} onClick={() => setInvoer(vanGegevens(gegevens))}>
          {t("common.cancel")}
        </Button>
        <Button disabled={bezig} onClick={opslaan}>{t("common.save")}</Button>
      </ActionBar>
    </section>
  );
}
